import { newId } from './ids';
import { ConversationStore } from './storage';
import {
  ConversationMessage,
  ConversationSession,
  ConversationTicket,
  CONVERSATION_SCHEMA_VERSION,
  MessageRating,
  SessionContext,
  SessionFeedback,
  TriageRecord,
} from './types';

/** A session with no activity for this long is archived and a fresh one started. */
export const SESSION_STALE_MS = 6 * 60 * 60 * 1000;

export type NewMessage = Omit<ConversationMessage, 'id' | 'createdAt'> & { createdAt?: number };

export interface ConversationService {
  /** Loads the current session, archiving it and starting fresh when stale. */
  getOrCreate(now?: number): Promise<ConversationSession>;
  getSession(): ConversationSession;
  append(message: NewMessage): Promise<ConversationMessage>;
  setMessageFeedback(messageId: string, rating: MessageRating): Promise<void>;
  setContext(partial: Partial<SessionContext>): Promise<void>;
  recordTriage(record: TriageRecord): Promise<void>;
  /** Replaces the session's ticket record (undefined clears it). */
  setTicket(ticket: ConversationTicket | undefined, failedAt?: number): Promise<void>;
  /** Ends the current session (optionally with the end-of-chat feedback) and starts a new one. */
  endSession(feedback?: Omit<SessionFeedback, 'submittedAt'>): Promise<ConversationSession>;
}

export const isSessionStale = (session: Pick<ConversationSession, 'lastActivityAt'>, now = Date.now()) =>
  session.lastActivityAt > 0 && now - session.lastActivityAt >= SESSION_STALE_MS;

export function createEmptySession(userKey: string, practiceId?: string, now = Date.now()): ConversationSession {
  return {
    schemaVersion: CONVERSATION_SCHEMA_VERSION,
    id: newId('session'),
    userKey,
    practiceId,
    startedAt: now,
    lastActivityAt: now,
    messages: [],
    context: {},
    triageRequests: [],
  };
}

export function createConversationService(
  store: ConversationStore,
  userKey: string,
  practiceId?: string
): ConversationService {
  let session: ConversationSession | null = null;

  const requireSession = (): ConversationSession => {
    if (!session) throw new Error('[askAi] conversation not initialised; call getOrCreate() first');
    return session;
  };

  const persist = async () => {
    await store.saveCurrent(requireSession());
  };

  return {
    async getOrCreate(now = Date.now()) {
      const stored = await store.loadCurrent(userKey);
      if (stored && !isSessionStale(stored, now)) {
        session = stored;
        return session;
      }
      if (stored) {
        await store.archive({ ...stored, endedAt: stored.endedAt ?? now });
      }
      session = createEmptySession(userKey, practiceId, now);
      await store.saveCurrent(session);
      return session;
    },

    getSession() {
      return requireSession();
    },

    async append(message) {
      const current = requireSession();
      const createdAt = message.createdAt ?? Date.now();
      const stored: ConversationMessage = { ...message, id: newId('msg'), createdAt };
      current.messages.push(stored);
      current.lastActivityAt = Math.max(current.lastActivityAt, createdAt);
      await persist();
      return stored;
    },

    async setMessageFeedback(messageId, rating) {
      const current = requireSession();
      const target = current.messages.find((m) => m.id === messageId);
      if (!target) return;
      target.feedback = rating;
      await persist();
    },

    async setContext(partial) {
      const current = requireSession();
      current.context = { ...current.context, ...partial };
      await persist();
    },

    async recordTriage(record) {
      const current = requireSession();
      current.triageRequests.push(record);
      await persist();
    },

    async setTicket(ticket, failedAt) {
      const current = requireSession();
      if (ticket) current.ticket = ticket; else delete current.ticket;
      if (failedAt !== undefined) current.ticketFailedAt = failedAt; else delete current.ticketFailedAt;
      await persist();
    },

    async endSession(feedback) {
      const current = requireSession();
      const now = Date.now();
      current.endedAt = now;
      if (feedback) current.sessionFeedback = { ...feedback, submittedAt: now };
      await store.archive(current);
      session = createEmptySession(userKey, practiceId, now);
      await store.saveCurrent(session);
      return session;
    },
  };
}
